import { rebalanceShares } from "../army-shares.js";
import type { SeatObservation } from "../types.js";
import type { HostMessage } from "./host-protocol.js";

type Shares = Readonly<Record<string, number>>;

// Presentation only: the authoritative relative order still settles at a game boundary.
export class ArmySharePreview {
  #draft: { id: string; base: Shares; value: number } | undefined;
  #pending: { requestId: string; shares: Shares; settled: boolean } | undefined;

  values(o: SeatObservation): Shares {
    const shares = this.#draft
      ? rebalanceShares(this.#draft.base, this.#draft.id, this.#draft.value)
      : (this.#pending?.shares ?? o.self.logistics.reinforcement?.shares["guard-robot"] ?? {});
    return Object.fromEntries(
      Object.keys(o.self.logistics.borders).map((id) => [id, shares[id] ?? 0]),
    );
  }

  preview(o: SeatObservation, id: string, value: number): void {
    if (!o.self.logistics.borders[id]) return;
    const base = this.#draft?.id === id ? this.#draft.base : this.values(o);
    this.#draft = { id, base, value };
  }

  commit(o: SeatObservation, id: string, value: number, requestId: string): void {
    const base = this.#draft?.id === id ? this.#draft.base : this.values(o);
    this.#pending = { requestId, shares: rebalanceShares(base, id, value), settled: false };
    this.#draft = undefined;
  }

  cancel(): void {
    this.#draft = undefined;
  }

  receive(message: HostMessage): void {
    if (message.kind === "command-result" && message.feedbackId === this.#pending?.requestId)
      this.#pending.settled = true;
    if (message.kind === "command-error" && message.feedbackId === this.#pending?.requestId)
      this.#pending = undefined;
    // The host publishes the resulting observation after its command receipts.
    if (message.kind === "observation" && this.#pending?.settled) this.#pending = undefined;
  }

  reset(): void {
    this.#draft = undefined;
    this.#pending = undefined;
  }
}
